import React, { useState } from "react";
import {
  X,
  Accessibility,
  Type,
  Palette,
  Eye,
  AlignLeft,
  MousePointer2,
  Contrast,
  ImageOff,
  PauseCircle,
  Link as LinkIcon,
  RotateCcw,
} from "lucide-react";

const PROFILES = [
  { id: "vision", label: "Vision Impaired Mode", desc: "Enhances website's visuals" },
  { id: "seizure", label: "Seizure Safe Profile", desc: "Clear flashes & reduces color" },
  { id: "adhd", label: "ADHD Friendly Mode", desc: "Focused browsing, distraction-free" },
  { id: "blindness", label: "Blindness Mode", desc: "Reduces distractions, improves focus" },
  { id: "epilepsy", label: "Epilepsy Safe Mode", desc: "Dims colors and stops blinking" },
];

const CONTENT_MODULES = [
  { id: "readable-font", label: "Readable Font", Icon: Type },
  { id: "big-cursor", label: "Big Cursor", Icon: MousePointer2 },
  { id: "align-text", label: "Align Text", Icon: AlignLeft },
  { id: "font-weight", label: "Font Weight", Icon: Type },
];

const COLOR_MODULES = [
  { id: "light-contrast", label: "Light Contrast", Icon: Palette },
  { id: "high-contrast", label: "High Contrast", Icon: Contrast },
  { id: "monochrome", label: "Monochrome", Icon: Eye },
];

const ORIENT_MODULES = [
  { id: "reading-line", label: "Reading Line", Icon: AlignLeft },
  { id: "hide-images", label: "Hide Images", Icon: ImageOff },
  { id: "stop-animations", label: "Stop Animations", Icon: PauseCircle },
  { id: "highlight-links", label: "Highlight Links", Icon: LinkIcon },
];

export default function AccessibilityPanel() {
  const [open, setOpen] = useState(false);
  const [fontSize, setFontSize] = useState(0);
  const [lineHeight, setLineHeight] = useState(0);
  const [profiles, setProfiles] = useState({});
  const [modules, setModules] = useState({});

  const applyFontSize = (n) => {
    const v = Math.max(-2, Math.min(4, n));
    setFontSize(v);
    document.documentElement.style.fontSize = `${100 + v * 10}%`;
  };
  const applyLineHeight = (n) => {
    const v = Math.max(-2, Math.min(4, n));
    setLineHeight(v);
    document.documentElement.style.setProperty(
      "--a11y-lh",
      (1.5 + v * 0.15).toString()
    );
    document.body.style.lineHeight = `${1.5 + v * 0.15}`;
  };
  const reset = () => {
    setProfiles({});
    setModules({});
    setFontSize(0);
    setLineHeight(0);
    document.documentElement.style.fontSize = "";
    document.body.style.lineHeight = "";
    document.documentElement.classList.remove(
      "a11y-mono",
      "a11y-highcontrast",
      "a11y-lightcontrast",
      "a11y-hide-images",
      "a11y-highlight-links",
      "a11y-stop-animations",
      "a11y-big-cursor",
      "a11y-readable-font"
    );
  };

  const toggleProfile = (id) => setProfiles((p) => ({ ...p, [id]: !p[id] }));
  const toggleModule = (id) => {
    const next = !modules[id];
    setModules((m) => ({ ...m, [id]: next }));
    const classMap = {
      "readable-font": "a11y-readable-font",
      "big-cursor": "a11y-big-cursor",
      "high-contrast": "a11y-highcontrast",
      "light-contrast": "a11y-lightcontrast",
      monochrome: "a11y-mono",
      "hide-images": "a11y-hide-images",
      "highlight-links": "a11y-highlight-links",
      "stop-animations": "a11y-stop-animations",
    };
    const cls = classMap[id];
    if (cls) {
      document.documentElement.classList.toggle(cls, next);
    }
  };

  return (
    <>
      <button
        data-testid="accessibility-toggle"
        aria-label="Toggle accessibility panel"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 left-4 z-[2100] w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#C99658] text-white shadow-[0_12px_30px_-10px_rgba(201,150,88,0.9)] border-2 border-white flex items-center justify-center hover:scale-105 transition-transform"
      >
        <Accessibility className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {open && (
        <div
          data-testid="accessibility-panel"
          className="fixed inset-0 z-[2200] flex justify-end"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <aside className="relative w-full md:w-[540px] h-full bg-neutral-50 shadow-2xl overflow-auto">
            <header className="bg-[#C99658] text-white px-6 py-10 text-center relative">
              <button
                data-testid="accessibility-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="w-12 h-12 mx-auto rounded-full bg-white/15 flex items-center justify-center mb-4">
                <Accessibility className="w-6 h-6" />
              </div>
              <h3 className="text-2xl md:text-[26px] font-semibold font-raleway">
                Accessibility Adjustments
              </h3>
              <p className="mt-2 text-white/85 text-[13px] font-nunito">
                Personalize your viewing experience
              </p>
            </header>

            <div className="p-4 md:p-6 space-y-5 -mt-8">
              <Card title="Select your accessibility profile">
                <div className="divide-y divide-neutral-100">
                  {PROFILES.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between py-3.5"
                    >
                      <div>
                        <div className="font-medium text-neutral-800 text-[14.5px]">
                          {p.label}
                        </div>
                        <div className="text-neutral-600 text-[12.5px] mt-0.5">
                          {p.desc}
                        </div>
                      </div>
                      <Toggle
                        checked={!!profiles[p.id]}
                        onChange={() => toggleProfile(p.id)}
                        testId={`a11y-profile-${p.id}`}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              <Card title="Content Modules">
                <div className="flex flex-wrap gap-3">
                  <Stepper
                    label="Font Size"
                    value={fontSize}
                    onInc={() => applyFontSize(fontSize + 1)}
                    onDec={() => applyFontSize(fontSize - 1)}
                  />
                  <Stepper
                    label="Line Height"
                    value={lineHeight}
                    onInc={() => applyLineHeight(lineHeight + 1)}
                    onDec={() => applyLineHeight(lineHeight - 1)}
                  />
                  {CONTENT_MODULES.map((m) => (
                    <Tile
                      key={m.id}
                      Icon={m.Icon}
                      label={m.label}
                      active={!!modules[m.id]}
                      onClick={() => toggleModule(m.id)}
                    />
                  ))}
                </div>
              </Card>

              <Card title="Color Modules">
                <div className="flex flex-wrap gap-3">
                  {COLOR_MODULES.map((m) => (
                    <Tile
                      key={m.id}
                      Icon={m.Icon}
                      label={m.label}
                      active={!!modules[m.id]}
                      onClick={() => toggleModule(m.id)}
                    />
                  ))}
                </div>
              </Card>

              <Card title="Orientation Modules">
                <div className="flex flex-wrap gap-3">
                  {ORIENT_MODULES.map((m) => (
                    <Tile
                      key={m.id}
                      Icon={m.Icon}
                      label={m.label}
                      active={!!modules[m.id]}
                      onClick={() => toggleModule(m.id)}
                    />
                  ))}
                </div>
              </Card>

              <button
                data-testid="accessibility-reset"
                onClick={reset}
                className="w-full mt-4 bg-[#C99658] hover:bg-neutral-900 text-white font-semibold py-3.5 rounded-md flex items-center justify-center gap-2 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset Settings
              </button>

              <p className="text-center text-[12px] text-neutral-400 pt-2 pb-8">
                Powered by ViewLux · accessibility built-in
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* Accessibility utility styles */}
      <style>{`
        .a11y-mono { filter: grayscale(1); }
        .a11y-highcontrast { filter: contrast(1.3) saturate(1.1); }
        .a11y-lightcontrast { filter: brightness(1.08) contrast(0.95); }
        .a11y-hide-images img, .a11y-hide-images iframe, .a11y-hide-images video { visibility: hidden !important; }
        .a11y-highlight-links a { background: #fff2c2 !important; box-shadow: inset 0 -2px 0 #C99658; }
        .a11y-stop-animations *, .a11y-stop-animations *::before, .a11y-stop-animations *::after { animation: none !important; transition: none !important; }
        .a11y-big-cursor, .a11y-big-cursor * { cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'><path fill='%23C99658' stroke='white' stroke-width='1.5' d='M3 2l7 20 3-9 9-3z'/></svg>") 6 4, auto !important; }
        .a11y-readable-font, .a11y-readable-font * { font-family: Arial, Helvetica, sans-serif !important; letter-spacing: 0.02em; }
      `}</style>
    </>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-[0_4px_24px_-18px_rgba(0,0,0,0.15)] p-5 md:p-6">
      <div className="text-neutral-900 font-semibold font-raleway text-[17px] mb-4">
        {title}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      data-testid={testId}
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        checked ? "bg-[#C99658]" : "bg-neutral-300"
      }`}
    >
      <span
        className={`absolute top-0.5 ${
          checked ? "left-5" : "left-0.5"
        } w-5 h-5 rounded-full bg-white shadow transition-all`}
      />
    </button>
  );
}

function Tile({ Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={`a11y-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
      className={`flex flex-col items-center gap-2.5 w-[calc(33.333%-0.5rem)] min-w-[120px] bg-neutral-50 border rounded-xl p-4 text-center transition-all ${
        active
          ? "border-[#C99658] ring-2 ring-[#C99658]/20 bg-white"
          : "border-transparent hover:border-neutral-200 hover:bg-white"
      }`}
    >
      <span className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-neutral-200 text-[#C99658]">
        <Icon className="w-4 h-4" />
      </span>
      <span className="text-[12.5px] text-neutral-800 font-medium">
        {label}
      </span>
    </button>
  );
}

function Stepper({ label, value, onInc, onDec }) {
  return (
    <div className="flex-1 min-w-[180px] bg-neutral-50 border border-transparent rounded-xl p-4">
      <div className="text-[13px] font-medium text-neutral-800 text-center mb-3">
        {label}
      </div>
      <div className="flex items-center justify-between bg-white rounded-full px-2 py-1.5">
        <button
          aria-label={`Decrease ${label}`}
          onClick={onDec}
          className="w-8 h-8 rounded-full bg-[#C99658] text-white font-bold"
        >
          −
        </button>
        <span className="text-[12px] font-medium text-neutral-600">
          {value === 0 ? "Default" : value > 0 ? `+${value}` : value}
        </span>
        <button
          aria-label={`Increase ${label}`}
          onClick={onInc}
          className="w-8 h-8 rounded-full bg-[#C99658] text-white font-bold"
        >
          +
        </button>
      </div>
    </div>
  );
}
